import React, { useCallback, useEffect, useState } from 'react'

import { Grid, Divider } from '@material-ui/core'
import PrintIcon from '@material-ui/icons/Print'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import DefaultErrorMessage from '../../../../../common/components/ErrorBoundary/errorMessage'
import StatelessDataTable from '../../../../../app/components/statelessDataTable'
import createColumns from '../columns/columns'
import service from '../../service'
import { filterLocales } from '../../../../../common/helpers'
import LEGACY_PRODUCTS from '../../../../../common/enum/legacyProducts'
import { Modal } from '../../../../../common/components/modal'
import CompanyLogo from '../../../../../app/components/companyLogo'
import { CSVDownloadError } from '../../../../../common/components/CSVDownloadError/CSVDownloadError'

const RemoteComplianceShipments = ({ companyId, hasPosterCheck }) => {
  const [loading, setLoading] = useState(Boolean(companyId))
  const [data, setData] = useState([])
  const [error, setError] = useState(false)
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchText, setSearchText] = useState('')
  const [sortOptions, setSortOptions] = useState({
    field: 'shipDate',
    direction: 'desc'
  })
  const [filterOptions, setFilterOptions] = useState({
    filter: '',
    field: ''
  })
  const [filters, setFilters] = useState({ state: '' })
  const [columns, setColumns] = useState(createColumns({
    sortOptions,
    filterOptions,
    eventHandlers: {
      setSortOptions,
      setFilterOptions,
      setPageNumber
    },
    hasPosterCheck
  }))
  const [orderModal, setOrderModalOpen] = useState(false)
  const [orderData, setOrderData] = useState('')
  const [orderAddress, setOrderAddress] = useState({})
  const [errorPopupOpen, setErrorPopupOpen] = useState(false)

  const [locales, setLocales] = useState([])

  dayjs.extend(utc)

  const handleClose = () => setErrorPopupOpen(false)

  const openOrderModal = (order) => {
    setOrderData(order)
    setOrderAddress(order.shipment.address)
    setOrderModalOpen(true)
  }

  const tableRow = (rowData) => {
    return (
      <tr>
        <td style={{ padding: '8px' }}>{rowData.itemName}</td>
        <td style={{ textAlign: 'center', padding: '8px' }}>{rowData.quantity}</td>
      </tr>
    )
  }

  const OrderModalContent = () => {
    return (
      <Grid container direction="column" style={{ padding: '8px 4px 4px 4px', textAlign: 'left', fontSize: '14px' }} className="text">
        <Grid item container justify="space-between">
          <Grid item container direction="column" xs={5}>
            <div><CompanyLogo color size="large" /></div>
            <span>Govdocs</span>
            <span>1305 Corporate Center Drive</span>
            <span>Ste 400</span>
            <span>Eagan, MN 55121</span>
            <span>(888) 273-3274</span>
          </Grid>
          <Grid item container xs={5} spacing={2} justify="flex-end">
            <Grid item container xs={5} direction="column" justify="space-between" style={{ fontWeight: '600' }}>
              <span>Order Date</span>
              <span>Order #</span>
              <span>Ship Date</span>
              <span>Shipping Method</span>
            </Grid>
            <Grid item container xs={6} direction="column" justify="space-between">
              <span>{dayjs.utc(orderData.shipment.shipDate).format('MM/DD/YYYY')}</span>
              <span>{orderData.order.orderNumber}</span>
              <span>{dayjs.utc(orderData.shipment.shipDate).format('MM/DD/YYYY')}</span>
              <span>{orderData.shipment.carrier.via}</span>
            </Grid>
          </Grid>
        </Grid>
        <Divider style={{ margin: '24px 16px 24px 0' }} />
        <Grid item container justify="space-between" style={{ marginBottom: '32px' }}>
          <Grid item xs={1} />
          <Grid item xs={9} container direction="column" style={{ flexWrap: 'nowrap', padding: '0 8px' }}>
            <Grid item container direction="column">
              <span style={{ marginBottom: '4px', fontWeight: '600' }}>Ship to</span>
              {orderAddress.attention && <span>{orderAddress.attention}</span>}
              {orderAddress.addressee && <span>{orderAddress.addressee}</span>}
              <span>{orderAddress.address1}</span>
              {orderAddress.address2 && <span>{orderAddress.address2}</span>}
              <span>{`${orderAddress.city}, ${orderAddress.state} ${orderAddress.zipCode}`}</span>
            </Grid>
            <Grid item>
              <table
                style={{
                  width: '100%',
                  border: '1px solid #E8E8E8',
                  marginTop: '16px',
                  borderCollapse: 'collapse'
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#E8E8E8', border: '1px solid #E8E8E8' }}>
                    <th style={{ width: '90%', padding: '8px', fontWeight: '600' }}>ITEM</th>
                    <th style={{ width: '10%', textAlign: 'center', padding: '8px', fontWeight: '600' }}>QTY</th>
                  </tr>
                </thead>
                <tbody>{orderData.items.map(tableRow)}</tbody>
              </table>
            </Grid>
          </Grid>
          <Grid item xs={1} container justify="center">
            {/* Icon is needed for when we need to download the pdf, so I'm leaving it commented until then */}
            {/* <PrintIcon /> */}
          </Grid>
        </Grid>
      </Grid>

    )
  }

  const filterLocalesList = (facets) => {
    const filteredLocales = filterLocales(LEGACY_PRODUCTS.POSTINGS, facets)
    setLocales(filteredLocales)
  }

  const rowsPerPage = 20

  useEffect(() => {
    let subscribed = true
    setLoading(true)
    setData([])
    service.queryShipments({
      recordType: 'shipments',
      searchText,
      sortOptions,
      filterOptions,
      pageNumber,
      rowsPerPage,
      companyId,
      filters
    })
      .then((json) => {
        if (subscribed) {
          setData(json.data.documents)
          filterLocalesList(json.facets)
          setTotal(json.data.count)
          setColumns(createColumns({
            sortOptions,
            filterOptions,
            eventHandlers: {
              setSortOptions,
              setFilterOptions,
              setPageNumber
            },
            hasPosterCheck,
            openOrderModal
          }))
          setPageNumber(pageNumber)
          setLoading(false)
        }
      })
      .catch((queryShipmentsError) => {
        setLoading(false)
        console.error('queryShipmentsError', queryShipmentsError || 'unknown error')
        setError(queryShipmentsError)
      })

    return () => (subscribed = false)
  }, [pageNumber, filters, searchText, sortOptions, filterOptions, companyId, hasPosterCheck])

  const handleChangePage = useCallback((event) => {
    setPageNumber(event.selected + 1)
    setData([])
    setLoading(true)
  }, [])

  const handleSetSearchText = useCallback((value) => {
    setSearchText(value)
  }, [])

  const handleSetSortOptions = useCallback((value) => {
    setSortOptions(value)
  }, [])

  const handleSetFilters = useCallback((value) => {
    setFilters(value)
  }, [])

  const handleSetPageNumber = useCallback((value) => {
    setPageNumber(value)
  }, [])

  const exportCSV = () => {
    service.queryShipments({
      recordType: 'shipments',
      searchText,
      sortOptions,
      filterOptions,
      pageNumber: 1,
      rowsPerPage: 1000000,
      companyId,
      filters,
      getCsv: true
    }).then((resultBodyJson) => {
      if (resultBodyJson && resultBodyJson.data.downloadUrl) {
        window.open(resultBodyJson.data.downloadUrl)
      } else {
        setErrorPopupOpen(true)
        throw Error('Shipment CSV generation error')
      }
    })
  }

  return (error)
    ? <DefaultErrorMessage error={error} data-testid="error-container" />
    : (
      <React.Fragment>
        <StatelessDataTable {...{
          pageCount: Math.ceil(total / rowsPerPage),
          isLoading: loading,
          showSearch: true,
          showExport: true,
          showLocaleFilter: true,
          eventHandlers: {
            handleChangePage,
            setSearchText: handleSetSearchText,
            setSortOptions: handleSetSortOptions,
            setFilters: handleSetFilters,
            export: exportCSV,
            setPageNumber: handleSetPageNumber
          },
          columns,
          data,
          filters,
          sortOptions,
          filterOptions,
          pageNumber,
          total,
          searchText,
          rowsPerPage,
          recordType: 'Shipments',
          locales
        }}
        />
        <CSVDownloadError open={errorPopupOpen} onClose={handleClose} />
        <Modal
          open={orderModal}
          modalCloseDispatch={() => { setOrderModalOpen(false) }}
          title="Order Information"
          styles={{ width: '860px' }}
          component={(<OrderModalContent />)}
        />
      </React.Fragment>
    )
}

export default RemoteComplianceShipments
